import PropTypes from 'prop-types'
import { useEffect, useLayoutEffect } from 'react'
import { useFlexLayout, usePagination, useSortBy, useTable } from 'react-table'
import clsx from 'clsx'
import Icon from '../Icon'
import { useTranslation } from 'react-i18next'

import style from './style.module.scss'
import Pagination from '../Pagination'
import ReactTooltip from 'react-tooltip'

// Elemente, die ihren Klick selbst verarbeiten. Ein Klick darauf darf nicht
// zusätzlich den Datensatz öffnen — sonst führt das Drei-Punkte-Menü einer
// anklickbaren Zeile beides zugleich aus.
const OWN_CLICK = 'button, a, input, select, textarea, label, [role="button"], [data-row-ignore]'

/**
 * Listentabelle.
 *
 * Zwei Festlegungen gelten für alle Listen gleich, damit dieselbe Art von
 * Datensatz überall gleich aussieht und sich gleich bedienen lässt:
 *
 * 1. **Die erste Spalte ist der Name des Datensatzes** — fett und in der
 *    dunklen Schrift, die übrigen Spalten in Türkis. Eine Ansicht, deren
 *    erste Spalte ausnahmsweise kein Name ist (die Rechnungsliste beginnt
 *    mit dem Status), setzt `primary: true` an der Spalte, die es ist.
 * 2. **Wo es einen Datensatz zu öffnen gibt, öffnet ihn die ganze Zeile**
 *    (`onRowClick`), nicht nur ein Wort darin. Mit der Tastatur geht das über
 *    Eingabe- und Leertaste.
 *
 * Spalten stehen links. Kurze Angaben, die man untereinander vergleicht —
 * Zahlen, Kürzel, Beträge — bekommen `align: 'center'` bzw. `'right'` an der
 * Spalte; Überschrift und Zellen richten sich dann gemeinsam aus.
 */
export default function Table(props) {
	const { i18n } = useTranslation()
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		state: { sortBy }
	} = useTable(
		{
			columns: props.columns,
			data: props.items,
			manualSortBy: true,
			// Sortiert wird auf dem Server. Ein dritter Klick hob die Sortierung
			// sonst auf: Das Pfeilsymbol verschwand, die Ansicht erfuhr davon aber
			// nichts, und die Liste blieb in der letzten Richtung stehen. So
			// wechselt jeder Klick nur noch die Richtung.
			disableSortRemove: true
		},
		useSortBy,
		usePagination,
		useFlexLayout
	)
	useLayoutEffect(() => {
		ReactTooltip.rebuild()
	}, [i18n.language])

	useEffect(() => {
		if (sortBy[0]) {
			const direction = sortBy[0].desc ? 'desc' : 'asc'
			props.handleSort(sortBy[0].id, direction)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortBy])

	// Ohne ausdrückliche Angabe trägt die erste Spalte den Namen.
	const hasOwnPrimary = props.columns.some(column => column.primary)
	const isPrimaryCell = (cell, index) => (hasOwnPrimary ? Boolean(cell.column.primary) : index === 0)

	const { onRowClick } = props

	const openRow = (row, event) => {
		if (event.target.closest && event.target.closest(OWN_CLICK)) return
		onRowClick(row.original, row)
	}

	return (
		<div className={style.root}>
			<div className={style.tableWrapper}>
				<div {...getTableProps()} className={style.table}>
					<div>
						{headerGroups.map(headerGroup => (
							<div {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map(column => {
									return (
										<>
											<ReactTooltip />
											<div
												data-tip={column.headerToolTip}
												className={clsx(
													style.title,
													column.align === 'center' && style.alignCenter,
													column.align === 'right' && style.alignRight,
													props.classNameHeader
												)}
												{...column.getHeaderProps(column.getSortByToggleProps())}
											>
												{column.render('Header')}

												{column.isSorted && (
													<>
														<Icon
															className={style.btnOrderAsc}
															name="swap"
															color={column.isSortedDesc ? '#008B9C' : '#285F74'}
															size={{ w: 6, h: 10 }}
														/>
														<Icon
															name="swap"
															color={column.isSortedDesc ? '#285F74' : '#008B9C'}
															size={{ w: 6, h: 10 }}
														/>
													</>
												)}
											</div>
										</>
									)
								})}
							</div>
						))}
					</div>
					<div {...getTableBodyProps()}>
						{rows.map(row => {
							prepareRow(row)
							const clickable = Boolean(onRowClick) && props.isRowClickable(row.original)
							return (
								<tr
									{...row.getRowProps()}
									className={clsx(
										style.row,
										clickable && style.clickable,
										props.getClassByRow && props.getClassByRow(row)
									)}
									title={clickable ? props.rowTitle : undefined}
									tabIndex={clickable ? 0 : undefined}
									onClick={clickable ? event => openRow(row, event) : undefined}
									onKeyDown={
										clickable
											? event => {
													if (event.key !== 'Enter' && event.key !== ' ') return
													// Die Leertaste blättert sonst die Seite weiter.
													event.preventDefault()
													openRow(row, event)
											  }
											: undefined
									}
								>
									{row.cells.map((cell, index) => (
										<td
											data-tip={(cell.column.cellToolTip && cell.column.cellToolTip(cell)) || ''}
											{...cell.getCellProps()}
											className={clsx(
												style.cell,
												isPrimaryCell(cell, index) && style.primaryCell,
												cell.column.align === 'center' && style.alignCenter,
												cell.column.align === 'right' && style.alignRight,
												props.classNameRow
											)}
										>
											{cell.render('Cell')}
										</td>
									))}
								</tr>
							)
						})}
					</div>
				</div>
			</div>

			{props.countAll > props.limit && (
				<Pagination
					limit={props.limit}
					page={props.page}
					label={props.paginationLabel}
					countAll={props.countAll}
					onChange={props.onChange}
				/>
			)}
		</div>
	)
}

Table.propTypes = {
	items: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	limit: PropTypes.number,
	countAll: PropTypes.number,
	onChange: PropTypes.func,
	handleSort: PropTypes.func,
	classNameHeader: PropTypes.string,
	classNameRow: PropTypes.string,
	/** Öffnet den Datensatz hinter der Zeile; ohne das bleibt die Zeile Text. */
	onRowClick: PropTypes.func,
	/** Einzelne Zeilen vom Öffnen ausnehmen (z. B. gelöschte Datensätze). */
	isRowClickable: PropTypes.func,
	/** Beschriftung des Mauszeigers über einer anklickbaren Zeile. */
	rowTitle: PropTypes.string
}
Table.defaultProps = {
	handleSort: () => {},
	isRowClickable: () => true
}
